import React, { useState, useEffect, useCallback } from 'react';
import {
  AppLayout,
  ContentLayout,
  Header,
  SpaceBetween,
  Table,
  Button,
  Box,
  Modal,
  FormField,
  Input,
  Alert,
  Select,
  Textarea,
  Toggle,
  Badge
} from '@amzn/awsui-components-core';
import { useAuth } from 'src/components/context/AuthContextProvider';
import { formatFullPdtTimestamp } from 'src/utilities/DateTimeUtilities';
import { HomePageSideNavigationV2 } from 'src/components/side-navigation-v2/HomePageSideNavigationV2';
import { logger } from 'src/logger';
import { LeaderCell } from 'src/components/das-finsuite/ea-huddle-v2/components/attendees/LeaderCell';
import { KINGPIN_GOAL_BASE_URL } from '../constants/KingpinUiConstants';
import { KingpinAdminService, KingpinGoalRegistry, KingpinGoalInput } from '../services/KingpinAdminService';

const TIER_OPTIONS = [
  { label: 'FGBS', value: 'FGBS' },
  { label: 'VP', value: 'VP' },
  { label: 'Team', value: 'Team' }
];

const KingpinGoalsAdminPage: React.FC = () => {
  const { Alias } = useAuth();
  const [goals, setGoals] = useState<KingpinGoalRegistry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [navigationOpen, setNavigationOpen] = useState(false);
  const [sortingColumn, setSortingColumn] = useState<{ sortingField: string }>({ sortingField: 'goalId' });
  const [sortingDescending, setSortingDescending] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [editingGoal, setEditingGoal] = useState<KingpinGoalRegistry | null>(null);
  const [saving, setSaving] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);

  const [goalId, setGoalId] = useState('');
  const [title, setTitle] = useState('');
  const [tier, setTier] = useState('');
  const [tobiasDirectAlias, setTobiasDirectAlias] = useState('');
  const [pocAliases, setPocAliases] = useState('');
  const [isActive, setIsActive] = useState(true);

  const [deleteTarget, setDeleteTarget] = useState<KingpinGoalRegistry | null>(null);
  const [deleting, setDeleting] = useState(false);

  const loadGoals = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await KingpinAdminService.listGoals();
      setGoals(data);
    } catch (err: unknown) {
      logger.error('Failed to load Kingpin goal registries', { error: err });
      setError('Failed to load goal registries.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadGoals();
  }, [loadGoals]);

  const resetForm = () => {
    setGoalId('');
    setTitle('');
    setTier('');
    setTobiasDirectAlias('');
    setPocAliases('');
    setIsActive(true);
  };

  const handleAdd = () => {
    resetForm();
    setEditingGoal(null);
    setModalError(null);
    setShowModal(true);
  };

  const handleEdit = (goal: KingpinGoalRegistry) => {
    setGoalId(goal.goalId);
    setTitle(goal.title ?? '');
    setTier(goal.tier ?? '');
    setTobiasDirectAlias(goal.tobiasDirectAlias ?? '');
    setPocAliases(goal.pocAliases?.join(', ') ?? '');
    setIsActive(goal.isActive ?? true);
    setEditingGoal(goal);
    setModalError(null);
    setShowModal(true);
  };

  const handleSave = async () => {
    setModalError(null);
    if (!goalId.trim()) {
      setModalError('Goal ID is required.');
      return;
    }

    if (!editingGoal && goals.some((g) => g.goalId === goalId.trim())) {
      setModalError(`A registry entry already exists for goal ${goalId.trim()}. Edit the existing entry instead.`);
      return;
    }

    setSaving(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const input: KingpinGoalInput = {
        goalId: goalId.trim(),
        tier: tier || undefined,
        title: title.trim() || undefined,
        tobiasDirectAlias: tobiasDirectAlias.trim() || undefined,
        pocAliases: pocAliases
          .split(',')
          .map((s) => s.trim().toLowerCase())
          .filter(Boolean),
        isActive,
        updatedBy: Alias
      };

      if (editingGoal) {
        await KingpinAdminService.updateGoal(input);
      } else {
        await KingpinAdminService.createGoal(input);
      }
      setShowModal(false);
      setSuccessMessage(`${editingGoal ? 'Updated' : 'Created'} goal registry for ${goalId.trim()}.`);
      await loadGoals();
    } catch (err: unknown) {
      logger.error('Failed to save Kingpin goal registry', { error: err });
      const message = err instanceof Error ? err.message : 'Failed to save goal registry.';
      setModalError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    const id = deleteTarget.goalId;
    setDeleting(true);
    setError(null);
    setSuccessMessage(null);
    try {
      await KingpinAdminService.deleteGoal(id, Alias);
      setDeleteTarget(null);
      setSuccessMessage(`Deactivated goal ${id}.`);
      await loadGoals();
    } catch (err: unknown) {
      logger.error('Failed to deactivate Kingpin goal', { error: err });
      setError('Failed to deactivate goal.');
    } finally {
      setDeleting(false);
    }
  };

  const columnDefinitions = [
    {
      id: 'goalId',
      header: 'Goal ID',
      cell: (item: KingpinGoalRegistry) => (
        <a href={`${KINGPIN_GOAL_BASE_URL}${item.goalId}`} target="_blank" rel="noopener noreferrer">
          {item.goalId}
        </a>
      ),
      sortingField: 'goalId'
    },
    {
      id: 'title',
      header: 'Title',
      cell: (item: KingpinGoalRegistry) => item.title || '—',
      sortingField: 'title'
    },
    {
      id: 'tier',
      header: 'Tier',
      cell: (item: KingpinGoalRegistry) => item.tier || '—',
      sortingField: 'tier'
    },
    {
      id: 'tobiasDirectAlias',
      header: 'Tobias Direct',
      cell: (item: KingpinGoalRegistry) => item.tobiasDirectAlias || '—',
      sortingField: 'tobiasDirectAlias'
    },
    {
      id: 'pocAliases',
      header: 'POC Aliases',
      cell: (item: KingpinGoalRegistry) => (item.pocAliases?.length ? item.pocAliases.join(', ') : '—')
    },
    {
      id: 'isActive',
      header: 'Active',
      cell: (item: KingpinGoalRegistry) => (
        <Badge color={item.isActive !== false ? 'green' : 'red'}>{item.isActive !== false ? 'Active' : 'Inactive'}</Badge>
      ),
      sortingField: 'isActive'
    },
    {
      id: 'createdBy',
      header: 'Created By',
      cell: (item: KingpinGoalRegistry) => (item.createdBy ? <LeaderCell alias={item.createdBy} /> : '—'),
      sortingField: 'createdBy'
    },
    {
      id: 'createdAt',
      header: 'Created',
      cell: (item: KingpinGoalRegistry) => formatFullPdtTimestamp(item.createdAt),
      sortingField: 'createdAt'
    },
    {
      id: 'updatedBy',
      header: 'Updated By',
      cell: (item: KingpinGoalRegistry) => (item.updatedBy ? <LeaderCell alias={item.updatedBy} /> : '—'),
      sortingField: 'updatedBy'
    },
    {
      id: 'updatedAt',
      header: 'Updated',
      cell: (item: KingpinGoalRegistry) => formatFullPdtTimestamp(item.updatedAt),
      sortingField: 'updatedAt'
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: (item: KingpinGoalRegistry) => (
        <SpaceBetween direction="horizontal" size="xs">
          <Button variant="icon" iconName="edit" ariaLabel="Edit" onClick={() => handleEdit(item)} />
          <Button variant="icon" iconName="remove" ariaLabel="Deactivate" onClick={() => setDeleteTarget(item)} disabled={item.isActive === false} />
        </SpaceBetween>
      )
    }
  ];

  return (
    <AppLayout
      navigation={<HomePageSideNavigationV2 />}
      navigationOpen={navigationOpen}
      onNavigationChange={({ detail }) => setNavigationOpen(detail.open)}
      contentType="table"
      maxContentWidth={Number.MAX_VALUE}
      toolsHide
      content={
        <ContentLayout header={<Header variant="h1">Kingpin Goals Registry</Header>}>
          <SpaceBetween size="l">
            {error && (
              <Alert type="error" dismissible onDismiss={() => setError(null)}>
                {error}
              </Alert>
            )}
            {successMessage && (
              <Alert type="success" dismissible onDismiss={() => setSuccessMessage(null)}>
                {successMessage}
              </Alert>
            )}

            <Table
              columnDefinitions={columnDefinitions}
              items={[...goals].sort((a, b) => {
                const field = sortingColumn.sortingField as keyof KingpinGoalRegistry;
                const aVal = String(a[field] ?? '');
                const bVal = String(b[field] ?? '');
                const cmp = aVal.localeCompare(bVal);
                return sortingDescending ? -cmp : cmp;
              })}
              loading={loading}
              loadingText="Loading goal registries..."
              empty={
                <Box textAlign="center" padding="l">
                  No goal registry entries found. Click &quot;Add Goal&quot; to create one.
                </Box>
              }
              header={
                <Header
                  variant="h2"
                  actions={
                    <SpaceBetween direction="horizontal" size="xs">
                      <Button iconName="refresh" onClick={loadGoals} disabled={loading}>
                        Refresh
                      </Button>
                      <Button variant="primary" onClick={handleAdd}>
                        Add Goal
                      </Button>
                    </SpaceBetween>
                  }
                  counter={`(${goals.length})`}
                >
                  Goal Registry Entries
                </Header>
              }
              sortingColumn={sortingColumn}
              sortingDescending={sortingDescending}
              onSortingChange={({ detail }) => {
                setSortingColumn(detail.sortingColumn as { sortingField: string });
                setSortingDescending(detail.isDescending ?? false);
              }}
              variant="full-page"
            />

            {showModal && (
              <Modal
                visible={true}
                onDismiss={() => setShowModal(false)}
                header={editingGoal ? 'Edit Goal Registry' : 'Add Goal Registry'}
                size="large"
                footer={
                  <Box float="right">
                    <SpaceBetween direction="horizontal" size="xs">
                      <Button variant="link" onClick={() => setShowModal(false)}>
                        Cancel
                      </Button>
                      <Button variant="primary" onClick={handleSave} loading={saving} disabled={!goalId.trim()}>
                        Save
                      </Button>
                    </SpaceBetween>
                  </Box>
                }
              >
                <SpaceBetween size="m">
                  {modalError && <Alert type="error">{modalError}</Alert>}
                  <FormField label="Goal ID" description="The Kingpin goal identifier (numeric ID from kingpin.amazon.com)">
                    <Input value={goalId} onChange={({ detail }) => setGoalId(detail.value)} placeholder="e.g., 1082495" disabled={!!editingGoal} />
                  </FormField>

                  <FormField label="Title">
                    <Input value={title} onChange={({ detail }) => setTitle(detail.value)} placeholder="Goal title for display" />
                  </FormField>

                  <FormField label="Tier">
                    <Select
                      selectedOption={tier ? { label: tier, value: tier } : null}
                      onChange={({ detail }) => setTier(detail.selectedOption.value ?? '')}
                      options={TIER_OPTIONS}
                      placeholder="Select tier"
                    />
                  </FormField>

                  <FormField label="Tobias Direct Alias" description="The direct report alias under which this goal is tracked">
                    <Input value={tobiasDirectAlias} onChange={({ detail }) => setTobiasDirectAlias(detail.value)} placeholder="e.g., jsmith" />
                  </FormField>

                  <FormField label="POC Aliases" description="Comma-separated list of point-of-contact aliases">
                    <Textarea value={pocAliases} onChange={({ detail }) => setPocAliases(detail.value)} placeholder="e.g., alias1, alias2" rows={2} />
                  </FormField>

                  <FormField label="Active">
                    <Toggle checked={isActive} onChange={({ detail }) => setIsActive(detail.checked)}>
                      {isActive ? 'Active' : 'Inactive'}
                    </Toggle>
                  </FormField>
                </SpaceBetween>
              </Modal>
            )}

            {deleteTarget && (
              <Modal
                visible={true}
                onDismiss={() => setDeleteTarget(null)}
                header="Confirm Deactivation"
                footer={
                  <Box float="right">
                    <SpaceBetween direction="horizontal" size="xs">
                      <Button variant="link" onClick={() => setDeleteTarget(null)}>
                        Cancel
                      </Button>
                      <Button variant="primary" onClick={handleDelete} loading={deleting}>
                        Deactivate
                      </Button>
                    </SpaceBetween>
                  </Box>
                }
              >
                Are you sure you want to deactivate goal <strong>{deleteTarget.goalId}</strong>
                {deleteTarget.title ? ` (${deleteTarget.title})` : ''}? The goal will be marked as inactive but not permanently deleted.
              </Modal>
            )}
          </SpaceBetween>
        </ContentLayout>
      }
    />
  );
};

export default KingpinGoalsAdminPage;
